import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { fmtDateTime } from '@/lib/format';
import { STAGE_LABEL } from '@/lib/roles';
import { ArrowLeft, History, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Iter38 — Legacy QC-gate history (read-only).
 *
 * Owner brief 2026-02: preserve every historical QC-gate row without
 * mixing it into the new sign-off workflow. This page renders the
 * archived qc_gates timeline for a single project.
 */
export default function QCHistoryPage() {
  const { id } = useParams();
  const [state, setState] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}/qc-history`)
      .then(r => setState(r.data))
      .catch(e => toast.error(parseErr(e)));
  }, [id]);

  if (!state) return <AppShell><div className="p-6">Loading QC history…</div></AppShell>;

  return (
    <AppShell breadcrumbs={[
      { label: 'Projects', to: '/projects' },
      { label: state.project_name, to: `/projects/${id}` },
      { label: 'QC gate history' },
    ]}>
      <div className="p-6 max-w-[1100px] mx-auto" data-testid="qc-history-page">
        <div className="flex items-center gap-3 mb-4">
          <Link to={`/projects/${id}`}
                className="chip inline-flex items-center gap-1"
                data-testid="qc-history-back">
            <ArrowLeft size={12} /> Back to project
          </Link>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <History size={20} /> QC gate history
          </h1>
        </div>

        <div className="border rounded-md p-3 mb-4 bg-[hsl(var(--surface-1))] text-[13px]"
             style={{ borderColor: 'hsl(var(--border))' }}
             data-testid="qc-history-notice">
          {state.note}
        </div>

        {(state.gates || []).length === 0 ? (
          <div className="text-[13px] text-[hsl(var(--foreground-muted))]" data-testid="qc-history-empty">
            No legacy QC-gate records exist for this project.
          </div>
        ) : (
          <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
               style={{ borderColor: 'hsl(var(--border))' }}>
            <table className="tbl" data-testid="qc-history-table">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>Signed by</th>
                  <th>Signed at</th>
                  <th>Notes</th>
                  <th>Checklist</th>
                </tr>
              </thead>
              <tbody>
                {(state.gates || []).map(g => (
                  <tr key={g.id} data-testid={`qc-history-row-${g.stage}`}>
                    <td className="font-medium">{STAGE_LABEL[g.stage] || g.stage}</td>
                    <td>
                      {g.status === 'passed'
                        ? <span className="chip chip-success"><CheckCircle2 size={10} className="mr-1" />Passed</span>
                        : g.status === 'needs_review'
                          ? <span className="chip chip-warning"><XCircle size={10} className="mr-1" />Needs review</span>
                          : <span className="chip">{g.status || '—'}</span>}
                    </td>
                    <td className="text-[12.5px]">{g.signed_off_by || '—'}</td>
                    <td className="text-[12px] text-[hsl(var(--foreground-muted))]">
                      {g.signed_off_at ? fmtDateTime(g.signed_off_at) : '—'}
                    </td>
                    <td className="text-[12.5px] max-w-[300px] whitespace-pre-wrap">{g.notes || '—'}</td>
                    <td className="text-[12px]">
                      {(g.checklist || []).length > 0
                        ? <span className="chip">{(g.checklist || []).filter(c => c.passed).length}/{(g.checklist || []).length} passed</span>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
